import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PowerPlantDetailHome from './PowerPlantDetailHome';
import PowerPlantTrendDetail from '../PowerPlantTrend/PowerPlantTrendDetail';
import PowerPlantReport from '../PowerPlantReport/PowerPlantReport';
import PowerPlantError from '../PowerPlantError/PowerPlantError';
import PowerPlantSettings from './PowerPlantSettings';
import CommonLayout from '../Common/CommonLayout';
import { 
  temperatureConfigAPI,
  panelConfigAPI,
  inverterConfigAPI,
  powerLocationAPI
} from '../../services/api';
import './PowerPlantDetailPage.css';

const PowerPlantDetailPage = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // URL에서 탭 정보 읽어오기 (기본값: home)
  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    const availableTabs = ['home', 'trend', 'report', 'error', 'display'];
    
    // 관리자 권한이 있는 경우에만 settings 탭 추가
    if (user?.role === 'ADMIN') {
      availableTabs.push('settings');
    }
    
    return availableTabs.includes(tab) ? tab : 'home';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [powerPlant, setPowerPlant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [configData, setConfigData] = useState({
    temperatureConfig: null,
    panelConfigs: [],
    inverterConfigs: []
  });

  useEffect(() => {
    const loadPowerPlantData = async () => {
      const plantId = parseInt(id);
      
      // ID가 유효한지 확인
      if (isNaN(plantId) || plantId <= 0) {
        console.error('Invalid plant ID:', id);
        navigate('/');
        return;
      }
      
      try {
        // 발전소 정보 가져오기 (권한 체크 포함)
        const powerPlantData = await powerLocationAPI.get(plantId);
        
        if (powerPlantData) {
          // API 데이터를 컴포넌트에서 사용하는 형식으로 변환
          setPowerPlant({
            id: powerPlantData.id,
            name: powerPlantData.locationName,
            region: powerPlantData.region || '',
            location: { 
              lat: powerPlantData.latitude, 
              lng: powerPlantData.longitude 
            },
            capacity: powerPlantData.capacity
          });
          
          // 설정 데이터 가져오기 (환경센서, 접속반, 인버터)
          try {
            const [temperatureConfig, panelConfigs, inverterConfigs] = await Promise.all([
              temperatureConfigAPI.checkTemperatureConfig(plantId).catch(() => null),
              panelConfigAPI.getPanelList(plantId).catch(() => []),
              inverterConfigAPI.getInverterList(plantId).catch(() => [])
            ]);
            
            setConfigData({
              temperatureConfig,
              panelConfigs,
              inverterConfigs
            });
          } catch (error) {
            console.error('Failed to load config data:', error);
            // 설정 데이터 로드 실패해도 계속 진행
          }
          
          setIsLoading(false);
        } else {
          console.error('Power plant not found for ID:', plantId);
          navigate('/');
        }
      } catch (error) {
        console.error('Failed to load power plant data:', error);
        setIsLoading(false);
        
        // 403 에러 (접근 권한 없음) 처리
        if (error.response?.status === 403) {
          // 접근 권한 없음 페이지로 이동
          navigate('/unauthorized');
        } else {
          // 다른 에러는 즉시 대시보드로 이동
          navigate('/');
        }
      }
    };
    
    loadPowerPlantData();
  }, [id, navigate]);

  // URL 변경 시 탭 상태 업데이트
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    const availableTabs = ['home', 'trend', 'report', 'error', 'display'];
    
    // 관리자 권한이 있는 경우에만 settings 탭 추가
    if (user?.role === 'ADMIN') {
      availableTabs.push('settings');
    }
    
    const newTab = availableTabs.includes(tab) ? tab : 'home';
    setActiveTab(newTab);
  }, [location.search, user?.role]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // URL에 탭 정보 추가
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tab);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const handleBack = () => {
    navigate('/');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home': 
        return <PowerPlantDetailHome powerPlant={powerPlant} configData={configData} />;
      case 'trend': 
        return <PowerPlantTrendDetail powerPlant={powerPlant} configData={configData} powerLocationId={powerPlant?.id} />;
      case 'report': 
        return <PowerPlantReport powerPlant={powerPlant} configData={configData} />;
      case 'error': 
        return <PowerPlantError powerPlant={powerPlant} configData={configData} />;
      case 'display': 
        return <div className="tab-content">전광판 페이지 (구현 예정)</div>;
      case 'settings':
        return <PowerPlantSettings powerPlant={powerPlant} user={user} onDelete={handleBack} />;
      default: 
        return <PowerPlantDetailHome powerPlant={powerPlant} configData={configData} />;
    }
  };

  if (isLoading) {
    return (
      <div className="power-plant-detail-loading">
        <div className="loading-spinner"></div>
        <p>발전소 정보를 불러오는 중...</p>
      </div>
    );
  }

  if (!powerPlant) {
    return (
      <div className="power-plant-detail-error">
        <h3>발전소를 찾을 수 없습니다</h3>
        <button onClick={handleBack} className="back-button">
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <CommonLayout 
      user={user} 
      onLogout={onLogout} 
      showTabs={true}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    >
      <div className="power-plant-detail-page">
        <div className="detail-content">
          {renderTabContent()}
        </div>
      </div>
    </CommonLayout>
  );
};

export default PowerPlantDetailPage;
